import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

import Spacer from '../Spacer';
import Label from './Label';

const Input = styled.input<{ color: string }>`
  outline: none;
  border: none;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 1px;
  color: ${(props) => props.color};
  padding: 11px 14px;
  background: none;
  width: 100%;
  outline: none;
  &::placeholder {
    color: #cfcfd3;
  }
`;

const InputContainer = styled.div<{
  borderColor?: string;
  background: string;
}>`
  outline: none;
  border: 1px solid ${(props) => props.borderColor};
  border-radius: 6px;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.2s;
  width: 100%;
  background: ${(props) => props.background};
  display: flex;
  align-items: center;
  padding-right: 18px;
`;

const Border = styled.div<{ highlightColor: string; fullWidth?: boolean }>`
  display: flex;
  padding: 5px;
  transition: all 0.2s;
  background: ${(props: any) => props.highlightColor};
  border-radius: 6px;
  ${(props) => (props.fullWidth ? ' width: 100%' : '')}
`;

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ErrorText = styled.div<{ shown: boolean }>`
  color: #ff5151;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  height: 17px;
  opacity: ${(props) => (props.shown ? '1' : '0')};
`;

type Props = {
  label?: string;
  value: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onChange?: (text: string, isValid: boolean) => void;
  placeholder?: string;
  fullWidth?: boolean;
  type?: string;
  isValid?: boolean;
  errorText?: string;
  validate?: 'email' | 'text' | 'password' | 'positive';
  validateImmediately?: boolean;
  showErrors?: boolean;
  inputStyles?: object;
  extraInfo?: React.ReactNode;
  rightExtraInfo?: React.ReactNode;
  disabled?: boolean;
};

function TextInput({
  label,
  value,
  onKeyDown,
  onChange,
  placeholder,
  fullWidth,
  type,
  validate,
  validateImmediately,
  isValid,
  errorText,
  showErrors,
  inputStyles,
  extraInfo,
  rightExtraInfo,
  disabled,
}: Props) {
  const [focused, setFocused] = useState(false);

  if (
    value !== '' &&
    isValid === undefined &&
    !errorText &&
    (!focused || validateImmediately)
  ) {
    switch (validate) {
      case 'email':
        isValid = RegExp(/.*@.*\..*/gm).test(value);
        if (!isValid) errorText = 'must be a valid email';
        break;
      case 'text':
        isValid = true;
        break;
      case 'password':
        isValid = value.length > 7;
        if (!isValid) errorText = 'must be at least 8 characters';
        break;
      case 'positive':
        let num = 0;
        isValid = false;
        try {
          num = parseFloat(value);
          if (num >= 0) isValid = true;
        } catch (err) {
          isValid = false;
        }
        break;
    }
  }

  var borderColor = '#e3e4e5';
  var highlightColor = 'transparent';
  if (focused) {
    borderColor = '#AEB1FC';
    highlightColor = '#f6f6ff';

    if (isValid === true) {
      highlightColor = '#F0FAF0';
      borderColor = '#A6E2A6';
    } else if (isValid === false) {
      highlightColor = '#FCEDF0';
      borderColor = '#FDB1B1';
    }
  } else {
    if (isValid === true) {
      borderColor = '#A6E2A6';
    } else if (isValid === false) {
      borderColor = '#FDB1B1';
    }
  }

  return (
    <>
      {(showErrors || label) && (
        <>
          <LabelContainer>
            {label && <Label>{label}</Label>}
            {rightExtraInfo ? (
              rightExtraInfo
            ) : (
              <ErrorText shown={!!errorText}>{errorText} </ErrorText>
            )}
          </LabelContainer>

          <Spacer vertical spacing={1} />
        </>
      )}
      {extraInfo}
      {extraInfo && <Spacer vertical spacing={1} />}
      <Border fullWidth={fullWidth} highlightColor={highlightColor}>
        <InputContainer
          borderColor={borderColor}
          background={disabled ? '#f7f7f7' : 'white'}
        >
          <Input
            disabled={disabled}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={onKeyDown}
            onChange={(evt) => {
              if (onChange) onChange(evt.target.value, !!isValid);
            }}
            placeholder={placeholder}
            type={type}
            value={value}
            style={inputStyles}
            color={disabled ? '#828190' : '#0c0833'}
          />
          {isValid === true && (
            <FontAwesomeIcon icon={faCheck} size={'xs'} color='#A6E2A6' />
          )}
          {isValid === false && (
            <FontAwesomeIcon icon={faTimes} size={'xs'} color='#FDB1B1' />
          )}
        </InputContainer>
      </Border>
    </>
  );
}

export default TextInput;
